import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Theme,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { FC, useMemo, useState } from 'react';

import { Piece, PieceAssetSize } from '@apiContract';
import { CameraCapture } from '@components/CameraCapture';
import { CameraPicker } from '@components/CameraPicker';
import { useCarousel } from '@components/Carousel';
import { DialogDarkFullscreen } from '@components/DialogDarkFullscreen';
import { Iconify } from '@components/Iconify';
import { ValidationData } from '@components/MailpieceActions/types';
import { useBarcodeScanner } from '@hooks/useBarcodeReader';
import useLocalStorage from '@hooks/useLocalStorage';
import { useSettings } from '@hooks/useSettings';

import { getPieceAsset } from '@utils/piece';

import { ConfirmValidationModal } from './components/ConfirmValidationModal';
import { Gallery } from './components/Gallery';
import { PieceThumbnails } from './components/PieceThumbnails';
import { ScanFeedbackCard } from './components/ScanFeedbackCard';
import { ValidationActionButtons } from './components/ValidationActionButtons';
import { ValidationMode, ValidationModeToggle } from './components/ValidationModeToggle';
import { useCameraValidation } from './hooks/useCameraValidation';
import { useQrCamera } from './hooks/useQrCamera';
import { useQrScanFeedback } from './hooks/useQrScanFeedback';
import { useValidationResults } from './hooks/useValidationResults';
import {
  sxAppBar,
  sxCameraWrapper,
  sxCapturedPreview,
  sxCounterChip,
  sxFeedbackChip,
  sxScannerModeOverlay,
  sxScannerPrompt,
  sxTitle,
  sxTopControls,
  sxVideoContainer,
  videoStyle,
} from './styles';

type ValidationScannerDialogProps = {
  open: boolean;
  title: string;
  onClose: VoidFunction;
  onQrCodeRecognized: (results: ValidationData) => void;
  onFinish?: (results: ValidationData, validatedIds: number[]) => void;
  piecesForValidate: Piece[];
  loading?: boolean;
  disableCloseButton?: boolean;
};

const ValidationScannerDialog: FC<ValidationScannerDialogProps> = ({
  open,
  title,
  onClose,
  onQrCodeRecognized,
  onFinish,
  piecesForValidate,
  loading = false,
  disableCloseButton = false,
}) => {
  const [galleryIndex, setGalleryIndex] = useState<number>(-1);
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

  const { LabelPrintingEnabled } = useSettings(['LabelPrintingEnabled']) as Record<string, boolean>;
  const [storedValidationMode, setValidationMode] = useLocalStorage<ValidationMode>('rd_validation_mode', 'scanner');
  const validationMode: ValidationMode = storedValidationMode || 'scanner';
  const isScannerMode = validationMode === 'scanner';

  const { validatedIds, isValidated, addValidation, getResults } = useValidationResults(
    piecesForValidate,
    onQrCodeRecognized,
  );

  // The gallery carousel lives here (not inside Gallery) because the currently viewed slide
  // drives this component's UI: the "Validated" chip and the "Validate with Image" button state.
  const galleryCarousel = useCarousel({ loop: false, startIndex: Math.max(galleryIndex, 0) });
  const previewIndex = galleryCarousel.dots.selectedIndex;

  const previewPieceId = piecesForValidate[previewIndex]?.id;

  const {
    cameraOpen,
    capturedImage,
    setCapturedImage,
    uploadingImage,
    openCamera,
    cancelCamera,
    retakePhoto,
    usePhoto,
  } = useCameraValidation({ pieceId: previewPieceId, isValidated, addValidation });

  const {
    feedbackChip,
    discreteScan,
    getTrackingNumbers,
    annotationsLoading,
    handleScannedData,
    handleDiscreteScan,
    scheduleLeaveReset,
  } = useQrScanFeedback({
    piecesForValidate,
    isValidated,
    addValidation,
    withTrackingNumbers: open && isScannerMode,
  });

  useBarcodeScanner(handleDiscreteScan, null, {
    enabled:
      open && !loading && isScannerMode && !annotationsLoading && !cameraOpen && !finishConfirmOpen && !exitConfirmOpen,
    minLength: 3,
    withPrint: false,
  });

  const { cameraDevices, setVideoRef, handleCameraChange } = useQrCamera({
    open,
    paused: cameraOpen || isScannerMode,
    onScan: handleScannedData,
    onDecodeError: scheduleLeaveReset,
  });

  const isVideoVisible = open && !loading;
  const isGalleryOpen = galleryIndex >= 0;
  const isValidateItemsScreen = !isGalleryOpen && !cameraOpen;
  const showFinishButton = !isGalleryOpen && piecesForValidate.length > 1;

  const isPreviewValidated = !!previewPieceId && validatedIds.includes(previewPieceId);

  const notValidatedCount = piecesForValidate.length - validatedIds.length;
  const notValidatedText = `${notValidatedCount} ${
    notValidatedCount === 1 ? 'item has' : 'items have'
  } not been validated. If you proceed, only validated items will be completed.`;

  const handleFinishConfirm = () => {
    setFinishConfirmOpen(false);
    onFinish?.(getResults(), validatedIds);
  };

  const handleExitConfirm = () => {
    setExitConfirmOpen(false);
    onClose();
  };

  const getHeaderTitle = () => {
    if (cameraOpen) return capturedImage ? 'Review' : 'Validate with Image';
    if (isGalleryOpen) return 'Preview';
    return title;
  };
  const headerTitle = getHeaderTitle();

  const gallerySlides = useMemo(
    () =>
      piecesForValidate.map((piece) => ({
        id: piece.id,
        src: getPieceAsset(piece.pieceAssets || [], PieceAssetSize.Large)?.url || '/images/empty_thumbnail.png',
        alt: `Piece ${piece.id}`,
      })),
    [piecesForValidate],
  );

  const handleHeaderClose = () => {
    if (cameraOpen) {
      cancelCamera();
      return;
    }
    if (isGalleryOpen) {
      setGalleryIndex(-1);
      return;
    }
    if (validatedIds.length > 0) {
      setExitConfirmOpen(true);
      return;
    }
    onClose();
  };

  // On sm/xs the action buttons move from the header to a bottom footer (matching the receive
  // session screens, which switch to bottom buttons below the md breakpoint).
  const isMobileView = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const actionButtonsProps = {
    isGalleryOpen,
    cameraOpen,
    hasCapturedImage: !!capturedImage,
    isPreviewValidated,
    uploadingImage,
    onValidateWithImage: () => {
      setGalleryIndex(previewIndex);
      openCamera();
    },
    onContinueGallery: () => setGalleryIndex(-1),
    onCancelCamera: cancelCamera,
    onRetakePhoto: retakePhoto,
    onUsePhoto: usePhoto,
  };

  // While the camera is live (before a photo is captured) the capture shutter already sits at the
  // bottom, so the Cancel button stays in the header on mobile instead of moving to the footer.
  const isCameraLive = cameraOpen && !capturedImage;

  return (
    <DialogDarkFullscreen open={open} onClose={onClose}>
      <AppBar sx={sxAppBar}>
        <Toolbar>
          <IconButton
            color="inherit"
            disableRipple
            disabled={disableCloseButton && !isGalleryOpen}
            aria-label={isGalleryOpen ? 'Close preview' : 'Close scanner'}
            onClick={handleHeaderClose}
          >
            <CloseIcon />
          </IconButton>

          <Typography sx={sxTitle} variant="h6" component="div">
            {headerTitle}
          </Typography>

          {isValidateItemsScreen && (
            <ValidationModeToggle
              mode={validationMode}
              onModeChange={setValidationMode}
              cameraDisabled={!LabelPrintingEnabled}
              hideLabels={isMobileView}
              sx={showFinishButton ? { mr: 2 } : undefined}
            />
          )}

          {showFinishButton && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setFinishConfirmOpen(true)}
              disabled={validatedIds.length === 0}
            >
              Finish
            </Button>
          )}

          {(!isMobileView || isCameraLive) && <ValidationActionButtons mobile={false} {...actionButtonsProps} />}
        </Toolbar>
      </AppBar>

      <Box sx={sxVideoContainer(feedbackChip?.color)}>
        {!isVideoVisible ? (
          <CircularProgress aria-label="Loading..." />
        ) : (
          <>
            <Box sx={sxTopControls}>
              <Chip
                variant="outlined"
                label={`${validatedIds.length}/${piecesForValidate.length}`}
                sx={sxCounterChip}
              />

              {!isScannerMode && <CameraPicker cameras={cameraDevices} onCameraChange={handleCameraChange} />}
            </Box>

            <video ref={setVideoRef} playsInline style={videoStyle}>
              <track kind="captions" />
            </video>

            {isScannerMode && (
              <Box sx={sxScannerModeOverlay}>
                {discreteScan ? (
                  <ScanFeedbackCard
                    result={discreteScan}
                    remainingCount={notValidatedCount}
                    trackingNumbers={discreteScan.piece ? getTrackingNumbers(discreteScan.piece.id) : []}
                  />
                ) : (
                  <Box sx={sxScannerPrompt}>
                    {annotationsLoading ? (
                      <CircularProgress />
                    ) : (
                      <Iconify icon="material-symbols:barcode-reader" width={64} sx={{ color: 'text.disabled' }} />
                    )}
                    <Typography variant="h6" color="common.white">
                      Scan to validate items
                    </Typography>
                    <Typography variant="body2">Using a connected scanner, scan any code on an item.</Typography>
                  </Box>
                )}
              </Box>
            )}

            {feedbackChip && !isScannerMode && (
              <Chip
                color={feedbackChip.color}
                icon={<feedbackChip.Icon />}
                label={feedbackChip.label}
                sx={sxFeedbackChip}
              />
            )}

            <PieceThumbnails pieces={piecesForValidate} validatedIds={validatedIds} onSelect={setGalleryIndex} />

            {isGalleryOpen && !cameraOpen && (
              <Gallery
                carousel={galleryCarousel}
                slides={gallerySlides}
                topLeftSlot={
                  isPreviewValidated ? <Chip color="success" icon={<CheckIcon />} label="Validated" /> : null
                }
              />
            )}

            {cameraOpen && (
              <Box sx={sxCameraWrapper}>
                <Box sx={{ display: capturedImage ? 'none' : 'flex', flex: 1 }}>
                  <CameraCapture capture={setCapturedImage} />
                </Box>
                {capturedImage && <Box component="img" src={capturedImage} alt="Captured" sx={sxCapturedPreview} />}
              </Box>
            )}
          </>
        )}
      </Box>

      {isMobileView && !isCameraLive && <ValidationActionButtons mobile {...actionButtonsProps} />}

      {exitConfirmOpen && (
        <ConfirmValidationModal
          title="Exit validation?"
          description="If you exit now, you’ll need to revalidate any items you’ve already scanned."
          confirmLabel="Exit"
          onClose={() => setExitConfirmOpen(false)}
          onConfirm={handleExitConfirm}
        />
      )}

      {finishConfirmOpen && (
        <ConfirmValidationModal
          title="Finish validation?"
          description={notValidatedText}
          confirmLabel="Finish"
          confirmVariant="contained"
          onClose={() => setFinishConfirmOpen(false)}
          onConfirm={handleFinishConfirm}
        />
      )}
    </DialogDarkFullscreen>
  );
};

export default ValidationScannerDialog;
